/* eslint-disable sort-keys */
/* eslint-disable @typescript-eslint/member-ordering */
import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { OrderingOption } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { FilterExpression, FilterOption, serializeMultiFieldSubExpressions } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { LocationsFilterOption } from '../models/locations-filter-option';
import { LocationsOrderingOptions, LocationsRegisterOptions } from '../models/locations-register-options';
import { GetLocations, GetLocationsVariables } from '../queries/generated/GetLocations';
import { getLocationsQuery } from '../queries/locations.graphql';

@Injectable({ providedIn: 'root' })
export class LocationsRegisterApiService {
   constructor(private apiService: LcsApiService) {}

   getLocations(
      endCursor: string | null,
      filters: LocationsFilterOption[] | null,
      quickFilters: LocationsRegisterOptions | null,
      orderingOptions: LocationsOrderingOptions[] | null
   ): Observable<ExecutionResult<GetLocations>> {
      const filterBuilderFilterExpression: FilterExpression = { filterOptions: [] };

      if (filters && filters.length > 0) {
         filterBuilderFilterExpression.filterOptions = filters.map(this.convertLocationFilterOption.bind(this));
      }

      if (quickFilters && quickFilters.searchText) {
         const searchWords = quickFilters.searchText.trim().split(/\s+/);
         const fieldsToSearch = [
            'Name',
            'HouseNumber',
            'StreetName',
            'DirectionalPrefix',
            'DirectionalPostfix',
            'StreetNameSuffix',
            'City',
            'State',
            'PostalCode',
         ];

         filterBuilderFilterExpression.subExpressions = searchWords.map((word) => {
            const wordSubExpression: FilterExpression = {
               filterOptions: fieldsToSearch.map((field) => {
                  return {
                     field: field,
                     operation: FilterOperations.Contains,
                     value: word,
                  };
               }),
               logicalOperator: LogicalOperators.Or,
            };

            return wordSubExpression;
         });

         filterBuilderFilterExpression.logicalOperator = LogicalOperators.And;
      }
      const variables = this.getLocationsVariables(endCursor, filterBuilderFilterExpression, orderingOptions);
      return this.apiService.query<GetLocations, GetLocationsVariables>({
         query: getLocationsQuery,
         variables,
      });
   }

   private convertLocationFilterOption(option: LocationsFilterOption): FilterOption {
      return {
         field: option.field,
         operation: option.operation,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(filterOption: LocationsFilterOption): FilterPrimitive | FilterPrimitive[] | null {
      if (filterOption.operation === FilterOperations.HasValue) {
         return filterOption.value;
      }

      switch (filterOption.type) {
         case 'location':
            return Array.isArray(filterOption.value)
               ? filterOption.value.map((v) => {
                    return v.locationId;
                 })
               : filterOption.value?.locationId || null;

         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return filterOption.value;
      }
   }

   private getLocationsVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetLocationsVariables {
      const variables: GetLocationsVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };
      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string | null {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeMultiFieldSubExpressions({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeMultiFieldSubExpressions({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeMultiFieldSubExpressions(expressions[0]);
      } else {
         return '';
      }
   }
}
